import { BigNum } from '@velocity-exchange/sdk';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import { cm } from 'src/utils/classNames';
import { useRef, useEffect, useState } from 'react';
import { getMarketPriceDecimals } from 'src/constants/marketDecimalConfig';
import {
	ORDERBOOK_DIVIDER_HEIGHT_MOBILE,
	ORDERBOOK_DIVIDER_HEIGHT_DESKTOP,
} from './OrderbookConstants';
import Env from 'src/environmentVariables/EnvironmentVariables';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { getFormattedSpreadQuote } from 'src/components/Orderbook/OrderbookUtils';
import { WithClassName } from 'src/utils/UtilityTypes';

interface Props extends WithClassName {
	midPrice: BigNum;
	spread: {
		quote: BigNum;
		quotePct: BigNum;
	};
	isMini?: boolean;
	tickSizeDecimalPlaces: number;
}

/**
 * Renders the mid price and spread (depending on screen size).
 */
export const OrderbookDivider = ({
	midPrice,
	spread,
	isMini,
	tickSizeDecimalPlaces,
	className,
}: Props) => {
	const currentMarket = useDriftStore((s) => s.selectedMarket.current);
	const isMobileScreenSize = useIsMobileScreenSize();

	const previousPrice = useRef<number | null>(null);
	const [priceDirection, setPriceDirection] = useState<'up' | 'down'>('up');

	const currentPrice = midPrice.toNum();

	useEffect(() => {
		if (
			previousPrice.current !== null &&
			previousPrice.current !== currentPrice
		) {
			setPriceDirection(currentPrice > previousPrice.current ? 'up' : 'down');
		}
		previousPrice.current = currentPrice;
	}, [currentPrice]);

	// Use market-specific decimal precision from configuration
	const baseAssetSymbol = currentMarket?.uiSymbols.baseAssetSymbol || '';
	const marketDecimals = getMarketPriceDecimals(baseAssetSymbol);

	const formattedMidPrice = currentPrice.toLocaleString(Env.locale, {
		minimumFractionDigits: marketDecimals,
		maximumFractionDigits: marketDecimals,
	});

	const formattedSpreadQuote = getFormattedSpreadQuote({
		baseAssetSymbol: currentMarket?.uiSymbols.baseAssetSymbol,
		tickSizeDecimalPlaces: tickSizeDecimalPlaces,
		spreadQuote: spread.quote,
	});

	const style = {
		height: isMobileScreenSize
			? ORDERBOOK_DIVIDER_HEIGHT_MOBILE
			: ORDERBOOK_DIVIDER_HEIGHT_DESKTOP,
	};

	return (
		<div
			className={cm(
				'inline-flex w-full items-center justify-between text-text-default',
				'border-y border-stroke-divider',
				'px-sm py-sm sm:py-md',
				className
			)}
			style={style}
		>
			<Tooltip content="Mark Price" side="left" decoration="none">
				<span
					className={cm(
						'typo-label-sm md:typo-heading-h5',
						priceDirection === 'up' && 'text-text-positive-green-button',
						priceDirection === 'down' && 'text-text-negative-red-button'
					)}
				>
					{formattedMidPrice}
				</span>
			</Tooltip>

			{!isMini && isMobileScreenSize && (
				<div className="flex items-end gap-x-2xs typo-body-xs text-text-secondary">
					<span>Spread:</span>
					<span>
						${formattedSpreadQuote} ({spread.quotePct.toFixed(3)}%)
					</span>
				</div>
			)}
		</div>
	);
};
